import { create } from 'zustand';

import { db } from '@/services/storage/db';
import { PropertyService } from '@/services/property-service';
import type { Property, CreatePropertyInput, UpdatePropertyInput } from '@/types/property';

const propertyService = new PropertyService(db);

interface PropertyStore {
  properties: Property[];
  isLoading: boolean;
  error: string | null;
  loadProperties: () => Promise<void>;
  getProperty: (id: string) => Promise<Property | null>;
  /** Returns the property with isPrimary=true, or null if no property is flagged. */
  getPrimary: () => Property | null;
  addProperty: (data: CreatePropertyInput) => Promise<Property>;
  editProperty: (id: string, data: UpdatePropertyInput) => Promise<Property>;
  removeProperty: (id: string) => Promise<void>;
  /** Atomically marks the target property primary and clears the flag on all others. */
  setPrimary: (propertyId: string) => Promise<void>;
}

export const usePropertyStore = create<PropertyStore>((set, get) => ({
  properties: [],
  isLoading: false,
  error: null,

  loadProperties: async () => {
    set({ isLoading: true, error: null });
    try {
      const properties = await propertyService.getProperties();
      set({ properties, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load properties';
      console.error('PropertyStore.loadProperties:', err);
      set({ error: message, isLoading: false });
    }
  },

  getProperty: async (id) => {
    // Check cache first
    const cached = get().properties.find((p) => p.id === id);
    if (cached) return cached;
    return propertyService.getProperty(id);
  },

  addProperty: async (data) => {
    try {
      const property = await propertyService.createProperty(data);
      set((state) => ({ properties: [...state.properties, property] }));

      // Fire-and-forget GMU backfill — do NOT await, caller gets property immediately.
      // db is DexieAdapter (see services/storage/db.ts) which has runPendingGmuBackfill.
      db.runPendingGmuBackfill()
        .then(() => get().loadProperties())
        .catch((err) => {
          console.error('[property-store] GMU backfill failed after create', err);
        });

      return property;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create property';
      console.error('PropertyStore.addProperty:', err);
      set({ error: message });
      throw err;
    }
  },

  editProperty: async (id, data) => {
    try {
      const updated = await propertyService.updateProperty(id, data);
      set((state) => ({
        properties: state.properties.map((p) => (p.id === id ? updated : p)),
      }));

      // If the update reset gmuStatus to 'pending' (location/state changed), run
      // the backfill pipeline so the GMU re-resolves immediately.
      if (updated.gmuStatus === 'pending') {
        db.runPendingGmuBackfill()
          .then(() => get().loadProperties())
          .catch((err) => {
            console.error('[property-store] GMU backfill failed after edit', err);
          });
      }

      return updated;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update property';
      console.error('PropertyStore.editProperty:', err);
      set({ error: message });
      throw err;
    }
  },

  removeProperty: async (id) => {
    try {
      await propertyService.deleteProperty(id);
      set((state) => ({ properties: state.properties.filter((p) => p.id !== id) }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete property';
      console.error('PropertyStore.removeProperty:', err);
      set({ error: message });
      throw err;
    }
  },

  getPrimary: () => get().properties.find((p) => p.isPrimary) ?? null,

  setPrimary: async (propertyId) => {
    try {
      await db.setPrimaryProperty(propertyId);
      set((state) => ({
        properties: state.properties.map((p) => ({ ...p, isPrimary: p.id === propertyId })),
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to set primary property';
      console.error('PropertyStore.setPrimary:', err);
      set({ error: message });
      throw err;
    }
  },
}));
